import React, { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import SearchIcon from '@mui/icons-material/Search';
import { TextField as MuiTextField } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { useGetList, useGetOne, useListContext, useNotify } from 'react-admin';

import InputSelectField from '../common/InputComponents/InputSelectField';

type SelectMode = 'subscriber' | 'screening' | 'none';

type SelectOption = {
  label: string;
  value: string;
};

export type ExportToolbarFiltersForm = Record<string, { label: string; value: string } | ''>;

export type BuildExportUrl = (args: {
  filterValues: Record<string, unknown>;
  sort?: { field: string; order?: string };
  total?: number;
}) => string;

export type ExportToolbarProps = {
  buildExportUrl: BuildExportUrl;
  selectMode?: SelectMode;
  selectLabel?: string;
  selectFilterKey?: string;
  searchPlaceholder?: string;
};

const getAuthToken = (): string => {
  const tokenString = localStorage.getItem('auth');
  if (!tokenString) {
    return '';
  }
  try {
    const parsed = JSON.parse(tokenString);
    return parsed?.data?.tokendata || '';
  } catch (error) {
    console.error('Error parsing auth token', error);
    return '';
  }
};

const ExportReportToolbar: React.FC<ExportToolbarProps> = ({
  buildExportUrl,
  selectMode = 'subscriber',
  selectLabel,
  selectFilterKey,
  searchPlaceholder = 'search',
}) => {
  const { filterValues, setFilters, sort, total, data } = useListContext<any>();
  const notify = useNotify();
  const [exporting, setExporting] = useState(false);
  const effectiveSelectMode: SelectMode = selectMode;
  const selectFieldName = selectFilterKey
    ?? (effectiveSelectMode === 'screening' ? 'screeningcompany' : effectiveSelectMode === 'subscriber' ? 'subscribername' : '');
  const effectiveSelectLabel = selectLabel
    ?? (effectiveSelectMode === 'screening' ? 'Screening Company:' : effectiveSelectMode === 'subscriber' ? 'Subscriber:' : '');

  const form = useForm<ExportToolbarFiltersForm>({
    defaultValues: selectFieldName ? { [selectFieldName]: '' } : {},
  });

  const [q, setQ] = useState<string>(filterValues?.q ?? '');

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters(
          {
            ...filterValues,
            q: value,
          },
          [],
        );
      }, 500),
    [filterValues, setFilters],
  );

  const subscriberListResult = useGetList('getsubscribers', {});
  const screeningCompanyResult = useGetOne(
    'screening-company',
    { id: 1 },
    { refetchOnWindowFocus: false },
  );

  const subscriberOptions: SelectOption[] = Array.isArray(subscriberListResult?.data)
    ? subscriberListResult.data.map((item: any) => ({ label: item.value, value: item.value }))
    : [];

  const screeningOptions: SelectOption[] = Array.isArray(screeningCompanyResult?.data?.data)
    ? screeningCompanyResult.data.data.map((item: any) => ({ label: item.Name, value: item.Name }))
    : [];

  const selectOptions = effectiveSelectMode === 'screening'
    ? screeningOptions
    : subscriberOptions;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    if (filterValues?.q === undefined) {
      setQ('');
      return;
    }
    setQ(filterValues.q);
  }, [filterValues?.q]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQ(value);
    debouncedSearch(value);
  };

  const handleSelectChange = () => {
    if (!selectFieldName) {
      return;
    }

    const selected = form.getValues(selectFieldName);
    const selectedValue = typeof selected === 'string' ? selected : selected?.value ?? '';
    setFilters(
      {
        ...filterValues,
        [selectFieldName]: selectedValue,
      },
      [],
    );
    form.reset({
      [selectFieldName]: '',
    });
  };

  const hasRecords = () => {
    if (typeof total === 'number') {
      return total > 0;
    }
    if (Array.isArray(data)) {
      return data.length > 0;
    }
    if (data && typeof data === 'object') {
      return Object.keys(data).length > 0;
    }
    return false;
  };

  const handleExport = async () => {
    const token = getAuthToken();
    if (!token) {
      notify('Authentication token missing. Please log in again.', {
        type: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    const exportUrl = buildExportUrl({ filterValues, sort, total: typeof total === 'number' ? total : undefined });
    if (!exportUrl) {
      notify('Unable to generate export URL.', {
        type: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return;
    }

    setExporting(true);
    try {
      const response = await fetch(exportUrl, {
        headers: {
          Authorization: token,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export report.');
      }

      const disposition = response.headers.get('content-disposition') || '';
      const raw = disposition.split('filename=')[1] || '';
      const filename = raw.split(';')[0]?.trim().replace(/^"|"$/g, '') || 'download';

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      notify('Report exported.', {
        type: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } catch (error: any) {
      console.error('Failed to export report', error);
      const message = error?.message || 'Unable to export report.';
      notify(message, {
        type: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="d-flex mb-2">
      {effectiveSelectMode !== 'none' && selectFieldName ? (
        <>
          <p style={{ paddingTop: 10 }}>{effectiveSelectLabel}</p>
          {/*<div style={{ zIndex: 1002, position: 'relative' }} className="px-2">*/}
          <div style={{ zIndex: 1002, position: 'relative', width: 'clamp(200px, 60vw, 320px)' }} className="px-2">
            <FormProvider {...form}>
              <InputSelectField
                isClearable={false}
                onChangeCallback={handleSelectChange}
                labelRequired={false}
                label=""
                options={selectOptions}
                source={selectFieldName}
                stopAutoFocus={true}
                required={false}
              />
            </FormProvider>
          </div>
        </>
      ) : null}
      <MuiTextField
        placeholder={searchPlaceholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        value={q}
        onChange={handleSearchChange}
        variant="outlined"
        size="small"
      />
      <button
        type="button"
        className="g-line-btn mx-2"
        title="Export all data"
        onClick={handleExport}
        disabled={exporting || !hasRecords()}
      >
        {exporting ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 50 50"
            xmlns="http://www.w3.org/2000/svg"
            className="spinner"
          >
            <circle
              cx="25"
              cy="25"
              r="20"
              stroke="#2DC3E8"
              strokeWidth="4"
              fill="none"
              strokeLinecap="round"
              strokeDasharray="31.4 31.4"
              strokeDashoffset="0"
              transform="rotate(-90 25 25)"
              style={{ animation: 'spin 1s linear infinite' }}
            />
          </svg>
        ) : (
          <svg
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_14375_6193)">
              <path
                d="M8.66667 8.4987H10.6667L8 11.1654L5.33333 8.4987H7.33333V5.83203H8.66667V8.4987ZM10 3.16536H3.33333V13.832H12.6667V5.83203H10V3.16536ZM2 2.49336C2 2.12803 2.298 1.83203 2.666 1.83203H10.6667L14 5.16536V14.494C14.0006 14.5816 13.984 14.6684 13.951 14.7495C13.9181 14.8306 13.8695 14.9045 13.808 14.9668C13.7466 15.0291 13.6734 15.0788 13.5928 15.1128C13.5121 15.1469 13.4255 15.1648 13.338 15.1654H2.662C2.48692 15.1641 2.31934 15.0941 2.19548 14.9704C2.07161 14.8466 2.0014 14.6791 2 14.504V2.49336Z"
                fill="#2DC3E8"
              />
            </g>
            <defs>
              <clipPath id="clip0_14375_6193">
                <rect width="16" height="16" fill="white" transform="translate(0 0.5)" />
              </clipPath>
            </defs>
          </svg>
        )}
      </button>
    </div>
  );
};

export default ExportReportToolbar;
